import { IndexedDBService } from '../indexeddb/indexed-db.service';
import { Injectable, inject } from '@angular/core';
import { DocumentUpdater, SystemHeaderFilter, UtilDocument, HTTP_STATUS_NOT_FOUND, UtilString } from '@formbird/shared';
import { ChangeSourceType, DocumentChangedInfo, FormParameters, SharedConstants } from '@formbird/types';
import { isEqual } from 'lodash';
import { cloneDeep } from 'es-toolkit';
import { formSetDocument } from '../../redux/actions';
import { IApplicationState } from '../../redux/state/application.state';
import { AppStore } from '../../redux/store/app.store';
import { BroadcastService, UpdatedFieldResult } from '../broadcast/broadcast.service';
import { ConfigService } from '../config/config.service';
import { ValidationService } from '../validation/validation.service';
import { DocumentService } from './document.service';
import { ModifiedFieldService } from './modified-field.service';
import { UnsavedDocumentService } from './unsaved-document.service';
import { ClientConstants } from '../../constants/ClientConstants';
import { DataService } from '../data/data.service';
import { OfflineStatusService } from '../offline-status/offline-status.service';
import { LoggedInUserService } from '../user/logged-in-user.service';
import { NotificationService } from '../notification/notification.service';

const logger = console;

@Injectable({
  providedIn: 'root'
})
export class DocumentUpdateService {
  private dataService = inject(DataService);
  private documentService = inject(DocumentService);
  private modifiedFieldService = inject(ModifiedFieldService);
  private unsavedDocumentService = inject(UnsavedDocumentService);
  private broadcastService = inject(BroadcastService);
  private configService = inject(ConfigService);
  private validationService = inject(ValidationService);
  private appStore = inject<AppStore<IApplicationState>>(AppStore);
  private offlineStatusService = inject(OfflineStatusService);
  private indexedDBService = inject(IndexedDBService);
  private loggedInUserService = inject(LoggedInUserService);
  private notificationService = inject(NotificationService);

  private updatePrevCurrentVersion(currentVersion) {

    const _self = this;

    // in case the provided version is current one so set currentVersion of the previous one to false
    // This case for updating document from pulling documents on reconnection

    const documentId = currentVersion.documentId;
    const loadedDocs = _self.unsavedDocumentService.getAllLoadedDocs();
    const orgDocs = _self.documentService.getOrgDocuments();
    const keys = Object.keys(orgDocs);

    for (let i = 0; i < keys.length; i++) {

      const versionId = keys[i];
      let orgDoc = orgDocs[versionId];

      if (documentId === orgDoc.documentId &&
        versionId !== documentId &&
        versionId !== currentVersion.systemHeader.versionId &&
        orgDoc.systemHeader.currentVersion
      ) {

        orgDoc = cloneDeep(orgDoc);
        orgDoc.systemHeader.currentVersion = false;
        _self.documentService.setOriginalDocumentOnly(orgDoc, orgDoc.systemHeader.versionId);

        loadedDocs.forEach(function(loadedDoc) {

          if (loadedDoc.systemHeader.versionId === orgDoc.systemHeader.versionId) {
            loadedDoc.systemHeader.currentVersion = false;
            _self.documentService.setLoadedDocumentOnly(cloneDeep(loadedDoc), orgDoc.systemHeader.versionId);
          }
        });

        return;
      }
    }
  }

  private executeOnFieldChangeOnPush(oldDocument, newDocument, changedFields) {

    if (!changedFields?.size || !oldDocument) {
      return;
    }

    const defaultExecuteOnFieldChangeOnPush = this.configService.clientConfig().defaultExecuteOnFieldChangeOnPush === true;
    const documentId = oldDocument.documentId;
    const documentListIdInfos = this.unsavedDocumentService.getDocumentListInfos(documentId);

    documentListIdInfos.forEach(documentListIdInfo => {

      const template = documentListIdInfo.template;
      const executeOnFieldChangeOnPush = template.executeOnFieldChangeOnPush === true;

      //check if the template has been enabled to execute OnFieldChange on push
      if (defaultExecuteOnFieldChangeOnPush || executeOnFieldChangeOnPush) {

        const formParameters: FormParameters = {
          unsavedDocumentListId: documentListIdInfo.documentListId,
          selectedDocumentId: documentId
        };

        changedFields.forEach(changedField => {

          const component = template?.components?.filter(comp => comp?.name === changedField)[0];
          if (component) {

            const fieldName = component.name;
            if (fieldName) {

              const documentChangedInfo: DocumentChangedInfo = {
                documentId: documentId,
                fieldName: fieldName,
                oldValue: oldDocument[fieldName],
                newValue: newDocument[fieldName],
                formParameters: formParameters,
                sourceType: ChangeSourceType.DOCUMENT_PUSH,
                sourceEvent: ChangeSourceType.DOCUMENT_PUSH,
                shouldCompareValues: false
              };

              this.validationService.executeOnFieldChangeWithNoChanges(documentChangedInfo);
            }
          }

        });
      }

    });


  }

  private extractChangedFields(updates) {

    const res = new Set<string>();

    updates.forEach(update => {
      const fieldName = update.path.split('/')[1];
      if (fieldName !== 'systemHeader') {
        res.add(fieldName);
      }
    });

    return res;

  }

  async updateVersions(versions) {

    const _self = this;

    if (versions && versions.length) {

      const currentVersion = versions.filter(version => version.systemHeader.currentVersion)[0];
      if (currentVersion) {

        const documentId = currentVersion.documentId;

        const loggedUserId = this.loggedInUserService.getUserId();
        if (documentId === loggedUserId) {
          await this.loggedInUserService.setUser(currentVersion);
          logger.info('User updated in memory: ' + documentId);
        }

        this.documentService.resetDisableSaveFieldToDoc(currentVersion);

        if (versions.length === 1) {
          _self.updatePrevCurrentVersion(currentVersion);
        }

        logger.info('Updating document in memory: ' + documentId);

        const loadedDoc = _self.documentService.getLoadedDocument(documentId);
        let isApplyChange =  true;
        if (_self.offlineStatusService.isCachingEnabled()) {
          const offlineCurrentVersion = await _self.dataService.getDocumentOfflineMode(documentId);
          const offlineVersionId = offlineCurrentVersion?.systemHeader?.versionId;
          const loadedDocVersionId = loadedDoc?.systemHeader?.versionId;
          const serverVersionId = currentVersion?.systemHeader?.versionId;

          
          const loadedDate = new Date(loadedDoc?.systemHeader?.createdDate || 0);
          const currentDate = new Date(currentVersion?.systemHeader?.createdDate || 0);


          isApplyChange = (offlineVersionId === loadedDocVersionId && loadedDocVersionId === serverVersionId) || 
              (currentDate > loadedDate) ;
        }

        if (loadedDoc?.systemHeader?.currentVersion && isApplyChange) {
          // use current version to update loaded document on form
          const changedFields = await _self.applyDocumentChanges(loadedDoc, currentVersion);

          // execute OnFieldChange on all the configured fields have been updated from pushed document
          this.executeOnFieldChangeOnPush(loadedDoc, currentVersion, changedFields);

          // update the loaded document in store
          _self.documentService.setLoadedDocumentOnly(loadedDoc);
          // use current version to update org version in store
          _self.documentService.setOriginalDocumentOnly(currentVersion);

          logger.info('Update document on form to latest one.');

          _self.broadcastService.broadcast(ClientConstants.DOCUMENT_UPDATED, [currentVersion]);
        }
      } else {
        if (_self.offlineStatusService.isCachingEnabled()) {
          await new Promise((res) => {
            _self.indexedDBService.getUnprocessedDocs(async function (unprocessedDocs) {
              const docs = unprocessedDocs.filter(doc => doc.documentId === versions[0].documentId);
              if (docs && docs.length === 0) {
                // in this case: all versions have been synched to server and there has no current version
                // --> show document deleted on form
                _self.broadcastService.broadcast(ClientConstants.DOCUMENT_DELETED, versions[0].documentId);
                res(true);
              }
            });
          });
        } else {
          _self.broadcastService.broadcast(ClientConstants.DOCUMENT_DELETED, versions[0].documentId);
        }

      }

      const nonCurrentVersions = versions.filter(version => version.systemHeader.currentVersion === false);
      if (nonCurrentVersions.length) {

        // user non current version to update loaded non-current version in store
        nonCurrentVersions.forEach(async function(version) {

          const versionId = version.systemHeader.versionId;
          const loadedVersion = _self.documentService.getLoadedDocument(versionId);

          if (loadedVersion) {
            await _self.applyDocumentChanges(loadedVersion, version);

            // update loaded non-current version on form
            _self.documentService.setLoadedDocumentOnly(cloneDeep(loadedVersion), versionId);
            // use non-current version to update its version in store
            _self.documentService.setOriginalDocumentOnly(version, versionId);
          }

        });
      }

    }
  }

  /**
   * Applies changes from the latest document version onto the current document, resolving conflicts
   * with any locally modified fields by delegating complex field updates to component handlers via broadcast.
   * @param currentDoc - The locally loaded document to apply updates onto.
   * @param latestDoc - The latest version of the document received from the server.
   * @param applyAllChanges - When true, also applies system header updates from latestDoc onto currentDoc.
   * @returns A Set of top-level field names that were changed on currentDoc.
   */
  async applyDocumentChanges(currentDoc, latestDoc, applyAllChanges?) {

    const _self = this;

    let changedFields = new Set<string>();

    const documentId = latestDoc.documentId;
    const templateId = currentDoc.systemHeader.templateId;
    logger.info('Applying document changes for doc: ' + documentId);

    const modifiedFields = _self.modifiedFieldService.getModifiedFieldsInDoc(documentId);

    var excludedOptions = {};
    excludedOptions[SharedConstants.MAKE_CURRENT_VERSION_KEY] = true;
    const loadedDocs = _self.unsavedDocumentService.getAllLoadedDocs(excludedOptions);

    for (let i = 0; i < loadedDocs.length; i++) {

      if (loadedDocs[i].documentId === documentId) {

        const loadedDoc = cloneDeep(loadedDocs[i]);

        const updates = await DocumentUpdater.getUpdates(currentDoc, latestDoc);

        if (updates.updates && updates.updates.length) {

          if (modifiedFields && modifiedFields.length) {

            const notifiedData: any = {};
            const orgDoc = _self.documentService.getExistingDocument(documentId);
            const orgCurrentUpdates = await DocumentUpdater.getUpdates(orgDoc, currentDoc);
            const orgLatestUpdates = await DocumentUpdater.getUpdates(orgDoc, latestDoc);

            for (let modifiedField of modifiedFields) {

              modifiedField = UtilString.convertToObjectPath(modifiedField, '/');
              
              notifiedData[modifiedField] = false;

              updates.updates.forEach(function(update: any, index: number) {

                const path = UtilString.convertToObjectPath(update.path, '/');

                if (modifiedField === path && !isEqual(loadedDoc[modifiedField], latestDoc[modifiedField])) {

                  logger.warn('Field in pushed document: \'' +
                    modifiedField + '\' has been changed to: ' + JSON.stringify(update.value));

                  delete updates.updates[index];

                }

                //complex data structure field, send to component handler to process it then assign the result value to the object.
                //if no handlers, ignore the pushed value
                if (_self.isComplexFieldPath(path, modifiedField)) {
                  
                  logger.warn('Field in pushed document: \'' +
                    modifiedField + '\' has been changed to: ' + JSON.stringify(update.value));

                  if (!notifiedData[modifiedField]) {
                    notifiedData[modifiedField] = {
                      documentId,
                      templateId,
                      modifiedField,
                      orgCurrentUpdates,
                      orgLatestUpdates,
                      orgDoc,
                      currentDoc,
                      latestDoc,
                      updates: []
                    };
                  }

                  notifiedData[modifiedField].updates.push(updates.updates[index]);

                  delete updates.updates[index];
                }

              });

              if (notifiedData[modifiedField]) {

                const res = await _self.broadcastService.onUpdatedFieldProcessed<any, UpdatedFieldResult>(notifiedData[modifiedField]);
                const items = res.filter((item) => item != undefined && item.processed);
                
                let warnMsg: any;
                if (items.length) {

                  const item = items[0];
                  currentDoc[modifiedField] = item.newValue;

                  console.log(item);
                  
                  if (item.conflicts?.length) {
                    warnMsg = `Field '${modifiedField}' has been changed in pushed document but conflicts not resolved.`;
                    console.warn(warnMsg);
                    console.warn(item.conflicts);
                  }

                } else {
                  console.warn(`Field '${modifiedField}' has been changed in pushed document but no handler processed the changes.`);
                  console.warn(notifiedData[modifiedField].updates);
                  // Push back updates for fields that were not processed by any handler
                  updates.updates.push(...notifiedData[modifiedField].updates);
                }

                if (warnMsg) {
                  _self.notificationService.printMessage(warnMsg, SharedConstants.STATUS_WARNING, '', '', true, '', '', 0, true);
                }

              }
            }

          }

          // Remove holes left by earlier `delete updates.updates[index]` calls
          updates.updates = updates.updates.filter((update: any) => update !== undefined);

          changedFields = _self.extractChangedFields(updates.updates);

          console.log('Updates: ', updates);
          console.log('ChangedFields: ', changedFields);

          if (changedFields?.size) {
            await DocumentUpdater.applyUpdates(currentDoc, updates);
          }

          if (applyAllChanges) {
            SystemHeaderFilter.applyUpdatesToSystemHeader(currentDoc, latestDoc);
          }
        }
      }
    }

    return changedFields;

  }

  /**
   * Checks whether an update path refers to a complex data structure nested under the modified field.
   * @param path - The object path of the update, using '.' as separator.
   * @param modifiedField - The top-level field path that was locally modified.
   * @returns True if the path is a nested child of modifiedField.
   */
  private isComplexFieldPath(path: string, modifiedField: string): boolean {
    return path.indexOf('.') !== -1 && path.startsWith(modifiedField);
  }

  async updateFormDocument(unsavedDocuments, doc) {

    if (unsavedDocuments.length) {

      // just mutate the document if so that references to this object are not cutoff
      // also update loaded docs too
      for (let j = 0; j < unsavedDocuments.length; j++) {
        const unsavedDoc = unsavedDocuments[j];

        if (unsavedDoc.documentId === doc.documentId) {

          const updates = await DocumentUpdater.getUpdates(unsavedDoc, doc);
          await DocumentUpdater.applyUpdates(unsavedDoc, updates);
          this.appStore.dispatch(formSetDocument(unsavedDoc));

          break;
        }
      }

    }
  }

  applyDocumentChangesSimple(unsavedDocument, doc) {
    DocumentUpdater.applyUpdatesSimple(unsavedDocument, doc);
  }

  async checkDocumentsCreated() {

    const _self = this;

    //should not perform checking and updating if caching is enabled
    if (this.offlineStatusService.isCachingEnabled()) {
      return;
    }

    //load all new documents on form
    const loadedDocuments = _self.unsavedDocumentService.getAllLoadedDocs();

    //check if any documents are new but found from server
    if (loadedDocuments && loadedDocuments.length) {

      loadedDocuments.forEach(async loadedDocument => {

        await this.applyDocumentCreated(loadedDocument);

      });
    }

  }

  async applyDocumentCreated(loadedDocument) {

    const _self = this;

    const documentId = loadedDocument.documentId;

    const isNew = UtilDocument.isNew(loadedDocument);
    if (!isNew) {
      return;
    }

    //do not check and update document if the pushing data is not reset to false because of errors in the previous save request
    if (!_self.modifiedFieldService.isPushing(documentId)) {
      logger.debug('Do not check document created for document: ' + documentId);
      return;
    }

    try {

      const serverDoc = await _self.dataService.getDocumentFromServer(documentId);
      if (serverDoc) {

        //Found from server. Update that new document using the version from server

        this.documentService.resetDisableSaveFieldToDoc(serverDoc);

        logger.info('Updating document in memory: ' + documentId);

        // use current version to update loaded document on form
        await _self.applyDocumentChanges(loadedDocument, serverDoc, true);

        // update the loaded document in store
        _self.documentService.setLoadedDocumentOnly(loadedDocument);
        // use current version to update org version in store
        _self.documentService.setOriginalDocumentOnly(serverDoc);

        logger.info('Update document on form to latest one.');

        _self.broadcastService.broadcast(ClientConstants.DOCUMENT_UPDATED, [serverDoc]);

      }

    } catch (error) {
      console.log(error?.status === HTTP_STATUS_NOT_FOUND ?
        `Document ${documentId} not found from server. Ignore updating on form.` :
        `Can not check document ${documentId} from server`
      );
    }
  }

}
